import { i18n } from 'i18next';

import {
  DEFAULT_LANG,
  DEFAULT_MANUAL_VALIDATION_NEEDED,
  MDIRECTOR_SUPPORT_PHONE,
  PAYMENTS_ROOT_URL,
  PHONE_VALIDATION_ACTIVATED,
  REGISTER_DOMAIN,
  SHOW_ANNUAL_PAYMENTS,
  THEME_ASSETS_URL,
  APP_BACKEND_URL,
  APP_EMBLUE_MD_HOME_URL,
} from '@/config';
import {
  ACTIVE,
  APP_LANGUAGES,
  BILLING_MODE_MONTH,
  BILLING_MODE_UNIQUE,
  BILLING_MODE_YEAR,
  CANCELLED,
  DATE_UNKNOWN,
  DEFAULT_CURRENCY_RESOLUTION_FOR_PRICES,
  DEFAULT_MISSING_EMAIL,
  DEFAULT_ORIGIN_FORM,
  DEFAULT_USER_CURRENCY,
  EUR,
  INTERVAL_COUNT_FOR_BILLING_MONTH,
  INTERVAL_COUNT_FOR_BILLING_UNIQUE,
  INTERVAL_COUNT_FOR_BILLING_YEAR,
  PENDING,
  PRIVATE_NAVIGATION_MY_ACCOUNT,
  PRIVATE_NAVIGATION_TAB_RATE,
  PROCESSING,
  PRODUCT_CATEGORY_FREEMIUM,
  PRODUCT_CATEGORY_PREPAID_EMAIL,
  PRODUCT_CATEGORY_PREPAID_SMS,
  PRODUCT_CATEGORY_TYPE_EMAIL,
  PRODUCT_CORE,
  PRODUCT_PAID_EXTERNAL,
  UNKNOWN_DATA,
  USAGE_LICENSED,
  USD,
  ROL_ADM_MDIRECTOR,
  ROL_ADM_MDIRECTOR_NEWFREE,
  ROL_STD_MDIRECTOR,
  ROL_REPORT_MDIRECTOR,
  ROL_ANALYST_MDIRECTOR,
  ROL_NDC_MDIRECTOR,
  ROL_DB_MDIRECTOR,
  ROL_CPM_MDIRECTOR,
  ROL_CPU_MDIRECTOR,
  ROL_GCYC_MDIRECTOR,
  ROL_CPM_REPORT_MDIRECTOR,
  PRODUCT_CATEGORY_SUSPENDED,
} from '@/config/appConstants';
import { BillingDetails } from '@/features/billing';
import { Country, CountryName } from '@/features/countries';
import { MobileToken } from '@/features/mobileToken';
import {
  BillingMode,
  IntervalBillingMode,
  Currency,
  currencySymbolMap,
  ICurrencyKeys,
  Me,
  UserData,
  PricesTypes,
  ProductDescriptionTypes,
  ProductTypes,
  StatusType,
  UsageType,
  UserProductTypes,
  GroupProductTypes,
  ProductCategoryTypes,
  IntervalBillingModes,
  BillingModes,
  AppLanguages,
  Theme,
  InvoicePreviewTypes,
  InvoicePreviewItemTypes,
} from '@/types';
import storage from '@/utils/storage';

export const getCurrencySymbol = (currency: Currency) =>
  currencySymbolMap[currency as keyof ICurrencyKeys];

export const getIntervalCountByBillingMode = (billingMode: BillingMode): IntervalBillingMode => {
  if (billingMode === BILLING_MODE_YEAR) {
    return INTERVAL_COUNT_FOR_BILLING_YEAR;
  } else if (billingMode === BILLING_MODE_MONTH) {
    return INTERVAL_COUNT_FOR_BILLING_MONTH;
  }

  return INTERVAL_COUNT_FOR_BILLING_UNIQUE;
};

export const getBillingModeByTargetTap = (target: string): BillingMode => {
  if (target === PRODUCT_CATEGORY_TYPE_EMAIL) {
    return BILLING_MODE_MONTH;
  }

  return BILLING_MODE_UNIQUE;
};

export const getBillingModeByIntervalCount = (intervalCount: IntervalBillingMode): BillingMode => {
  if (intervalCount === INTERVAL_COUNT_FOR_BILLING_YEAR) {
    return BILLING_MODE_YEAR;
  }

  if (intervalCount === INTERVAL_COUNT_FOR_BILLING_MONTH) {
    return BILLING_MODE_MONTH;
  }

  return BILLING_MODE_UNIQUE;
};

export const getIntervalBillingModeFromUserProduct = (
  userProduct: UserProductTypes
): IntervalBillingMode => {
  const groupProduct: GroupProductTypes | null = userProduct.groupProduct || null;

  if (!groupProduct || !groupProduct.pricing || groupProduct.pricing.length === 0) {
    return INTERVAL_COUNT_FOR_BILLING_MONTH;
  }

  const licensedPricing = getLicensedPrice(groupProduct.pricing);

  if (!licensedPricing) {
    return INTERVAL_COUNT_FOR_BILLING_MONTH;
  }

  return licensedPricing.recurring?.interval_count as IntervalBillingMode;
};

export const getBillingModeFromUserProduct = (
  userProduct: UserProductTypes | null | undefined
): BillingMode => {
  if (!userProduct) {
    return BILLING_MODE_MONTH;
  }

  return getBillingModeByIntervalCount(getIntervalBillingModeFromUserProduct(userProduct));
};

export const getBillingModePriceText = (userBillingMode: BillingMode): string => {
  switch (userBillingMode) {
    case BILLING_MODE_YEAR:
      return 'ProductDetail.yearlyPrice';
    case BILLING_MODE_UNIQUE:
      return 'ProductDetail.uniquePrice';
    case BILLING_MODE_MONTH:
    default:
      return 'ProductDetail.monthlyPrice';
  }
};

export const getBillingModePlanText = (userBillingMode: BillingMode): string => {
  switch (userBillingMode) {
    case BILLING_MODE_YEAR:
      return 'ProductDetail.yearlyPlan';
    case BILLING_MODE_MONTH:
    default:
      return 'ProductDetail.monthlyPlan';
  }
};

export const getProductPrice = (
  product: ProductTypes,
  type: UsageType,
  currency: Currency,
  billingMode: BillingMode
) => {
  const intervalCount = getIntervalCountByBillingMode(billingMode);

  const prices = product.prices.find(
    (price) =>
      price.currency === currency &&
      ((price.recurring == null && type === USAGE_LICENSED) ||
        (price.recurring &&
          price.recurring.usage_type === type &&
          price.recurring.interval === BILLING_MODE_MONTH &&
          price.recurring.interval_count === intervalCount))
  );

  return prices || false;
};

export const getLicensedPrice = (prices: PricesTypes[]) => {
  if (prices.length === 1) {
    return prices[0];
  }

  return prices.filter(({ recurring }) => recurring?.usage_type === USAGE_LICENSED)[0];
};

export const getLicensedPriceByInterval = (
  prices: PricesTypes[],
  intervalCount: IntervalBillingMode,
  currency: Currency
) => {
  if (prices.length === 1) {
    return prices[0];
  }

  return prices.filter(
    ({ recurring, currency: currencyPrice }) =>
      recurring?.usage_type === USAGE_LICENSED &&
      recurring?.interval_count === intervalCount &&
      currencyPrice === currency
  )[0];
};

export const classToggle = (el: HTMLElement, ...args: string[]) =>
  args.map((e) => el.classList.toggle(e));

export const getFreemiumProduct = (products: ProductTypes[]) =>
  products.filter((product) => product.category === PRODUCT_CATEGORY_FREEMIUM)[0];

export const getProductsNonFreemium = (products: ProductTypes[]) =>
  products.filter((product) => product.category !== PRODUCT_CATEGORY_FREEMIUM);

export const getMDirectorSupportPhone = () => MDIRECTOR_SUPPORT_PHONE;

export const getMDirectorSupportPhoneRaw = () =>
  MDIRECTOR_SUPPORT_PHONE && MDIRECTOR_SUPPORT_PHONE.replace(/\s/g, '');

export const getUserCurrentProductFromProducts = (userProducts: UserProductTypes[]) => {
  const userProductActive = filterUserProductsByStatus(userProducts, ACTIVE);
  const userProductCancelled = filterUserProductsByStatus(userProducts, CANCELLED);
  const userProductProcessing = filterUserProductsByStatus(userProducts, PROCESSING);
  const userProductPending = filterUserProductsByStatus(userProducts, PENDING);

  return userProductActive || userProductCancelled || userProductProcessing || userProductPending;
};

export const haveUserAnOldPlan = (userProducts: UserProductTypes[]) =>
  !getUserCurrentProductFromProducts(userProducts);

export const getUserProduct = (userProducts: UserProductTypes[]) => userProducts[0]?.product;

export const getUserProductSubscriptionId = (userProducts: UserProductTypes[]) =>
  userProducts[0].groupProduct.subscriptionId;

export const isExternalPaidProduct = (userProducts: UserProductTypes[]) => {
  const userProduct = getUserProduct(userProducts);
  return userProduct && getUserProductSubscriptionId(userProducts) === PRODUCT_PAID_EXTERNAL;
};

export const isExternalFreeProduct = (userProducts: UserProductTypes[]) => {
  const userProduct = getUserProduct(userProducts);

  return (
    userProduct &&
    isFreemiumProduct(userProduct) &&
    getUserProductSubscriptionId(userProducts) === PRODUCT_PAID_EXTERNAL
  );
};

export const isCoreProduct = (product: ProductTypes): boolean => {
  return product.class === PRODUCT_CORE;
};

export const isFreemiumProduct = (product: ProductTypes | null | undefined): boolean => {
  if (!product) return false;
  return product.category === PRODUCT_CATEGORY_FREEMIUM;
};

export const isSuspendedProduct = (product: ProductTypes | null | undefined): boolean => {
  if (!product) return false;
  return product.category === PRODUCT_CATEGORY_SUSPENDED;
};

export const isPrepaidProduct = (product: ProductTypes | null): boolean => {
  if (!product) return false;

  return (
    product.category === PRODUCT_CATEGORY_PREPAID_EMAIL ||
    product.category === PRODUCT_CATEGORY_PREPAID_SMS
  );
};

export const userHasProducts = (userProducts: UserProductTypes[] | undefined): boolean => {
  if (!userProducts) {
    return false;
  }

  return userProducts.length > 0;
};

export const getNoPlanProduct = (
  products: ProductTypes[][],
  marks: any,
  productStep = 0
): ProductTypes => {
  const stepValue = marks[productStep];
  const filteredProducts = products.map((product: ProductTypes[]) => {
    const items = product.filter((item: ProductTypes) => {
      return item.info.productStep === stepValue;
    });
    return items && items[0];
  });
  return filteredProducts.filter((item) => !!item)[0];
};

export const getProductDetails = (product: ProductTypes, prop: keyof ProductDescriptionTypes) =>
  product.info[prop] || '';

export const getProductStep = (userProduct: UserProductTypes) =>
  userProduct.product.info.productStep;

export const getKeyByValue = (object: any, value: string | number) =>
  Object.keys(object).find((key) => object[key] === value);

export const isCurrency = (currency: Currency): boolean => currency === EUR || currency === USD;

export const isBillingMode = (billingMode: BillingMode): boolean =>
  BillingModes.includes(billingMode);

export const isLanguage = (lang: string | undefined): boolean => {
  if (lang) return APP_LANGUAGES.includes(lang);
  return false;
};

export const isIntervalBillingMode = (interval: any): boolean =>
  IntervalBillingModes.includes(parseInt(interval, 10));

export const getUserCurrency = (
  billingDetails: BillingDetails | null | undefined,
  storage: any
): Currency => {
  if (storage.getUserCurrency && storage.getUserCurrency()) {
    return storage.getUserCurrency() as Currency;
  }

  if (billingDetails && billingDetails.currency) {
    return billingDetails.currency as Currency;
  }

  return DEFAULT_USER_CURRENCY;
};

export const generatePendingUserProductTypes = (product: ProductTypes): UserProductTypes => {
  return {
    product: product,
    invoicePreview: null,
    groupProduct: {
      status: PENDING,
      productId: product.mdirectorId,
      class: product.class,
      pricing: product.prices,
    },
  };
};

export const buildProductsCheckingInStorageForPendings = (
  userProducts: UserProductTypes[] | undefined,
  storage: any
): UserProductTypes[] | undefined => {
  const product = storage.getPendingProduct();
  if (product) {
    if (!userProducts) {
      return [generatePendingUserProductTypes(product)];
    }
    userProducts.push(generatePendingUserProductTypes(product));
  }

  return userProducts;
};

export const checkHasToShowUserAccountForm = (
  currency: Currency,
  me: Me,
  billingDetails?: BillingDetails
): boolean => {
  if (!billingDetails || !currency || !me.capabilities.isBillingDetailsFilled) {
    return true;
  }

  if (billingDetails.currency?.toLowerCase() !== currency.toLowerCase()) {
    return true;
  }

  return false;
};

export const checkByStatus = (
  userProducts: UserProductTypes[] | undefined,
  mdirectorId: string,
  status: StatusType[],
  billingMode: BillingMode
) => {
  if (!userProducts) {
    return false;
  }
  const intervalCount = getIntervalCountByBillingMode(billingMode);

  return !!userProducts.filter((userProduct) => {
    if (userProduct.groupProduct.pricing) {
      const licensedPrice = getLicensedPrice(userProduct.groupProduct.pricing);

      return (
        status.includes(userProduct.groupProduct.status as StatusType) &&
        userProduct.product.mdirectorId === mdirectorId &&
        (licensedPrice ? licensedPrice.recurring?.interval_count === intervalCount : true)
      );
    }

    return (
      status.includes(userProduct.groupProduct.status as StatusType) &&
      userProduct.product.mdirectorId === mdirectorId
    );
  }).length;
};

export const filterUserProductsByStatus = (
  userProducts: UserProductTypes[] | null | undefined,
  status: StatusType
): UserProductTypes | null => {
  const products =
    userProducts &&
    userProducts.filter(
      (product: UserProductTypes) => product && product.groupProduct.status === status
    );

  if (products && products[0]?.product && products[0]?.groupProduct.pricing) {
    products[0].product.prices = products[0].groupProduct.pricing;
  }

  return (products && products[0]) || null;
};

export const filterUserProductsByPending = (userProducts: UserProductTypes[]) =>
  filterUserProductsByStatus(userProducts, PENDING);

export const filterUserProductsByActive = (userProducts: UserProductTypes[]) =>
  filterUserProductsByStatus(userProducts, ACTIVE);

export const parseDate = (locale: string = DEFAULT_LANG, date: string, options = {}) => {
  try {
    const dateObj = new Date(date);
    return new Intl.DateTimeFormat([locale], options).format(dateObj);
  } catch (error) {
    return DATE_UNKNOWN;
  }
};

export const parseCurrency = (
  locale: string,
  currency: Currency,
  price: number | bigint,
  resolution: number = DEFAULT_CURRENCY_RESOLUTION_FOR_PRICES
) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: resolution,
  }).format(price);

export const getPriceText = (
  price: number | bigint | string,
  currency: Currency,
  language: string
): string => {
  if (price === null) {
    return UNKNOWN_DATA;
  }

  if (typeof price === 'string') {
    return price;
  }

  return parseCurrency(language, currency, price);
};

export const getPendingUserProduct = (
  userProducts: UserProductTypes[] | null
): UserProductTypes | null => filterUserProductsByStatus(userProducts, PENDING);

export const getActiveUserProduct = (
  userProducts: UserProductTypes[] | null | undefined
): UserProductTypes | null => filterUserProductsByStatus(userProducts, ACTIVE);

export const searchProductByPriceId = (products: ProductTypes[], priceId: string) => {
  const filteredProducts: ProductTypes[] = products.filter(({ prices = [] }) =>
    prices.some(({ id }) => id === priceId)
  );
  if (filteredProducts.length > 0) {
    filteredProducts[0].prices = filteredProducts[0].prices.filter(({ id }) => id === priceId);
  }
  return filteredProducts;
};

export const getCurrencyFromPriceId = (product: ProductTypes, priceId: string) =>
  product.prices.filter(({ id }) => id === priceId)[0].currency;

export const getCurrencyFromGroupProduct = (groupProduct: GroupProductTypes) => {
  if (!groupProduct || !groupProduct.pricing || !groupProduct.pricing.length) {
    return DEFAULT_USER_CURRENCY;
  }

  const pricing: PricesTypes = groupProduct.pricing[0];
  return pricing.currency as Currency;
};

export const getCurrencyFromUserProduct = (userProduct: UserProductTypes) => {
  const groupProduct: GroupProductTypes | null = userProduct.groupProduct || null;

  return getCurrencyFromGroupProduct(groupProduct);
};

export const getProductLicensedPriceByCurrency = (prices: PricesTypes[], currency: Currency) => {
  const results = prices.filter((price) => price.currency === currency);

  return results.length === 1
    ? results[0]
    : results.filter(({ recurring }) => recurring?.usage_type === USAGE_LICENSED)[0];
};

export const getPrepaidProductPriceByCurrency = (prices: PricesTypes[], currency: Currency) => {
  const results = prices.filter((price) => price.currency === currency);
  const price = results[0]?.amount;

  if (!price) {
    return UNKNOWN_DATA;
  }

  if (typeof price === 'string') {
    return parseFloat(price.replace(',', '.') || '0');
  }

  return price;
};

export const sortCountries = (countries: Country[], userCountry: keyof CountryName) =>
  countries.sort((a: Country, b: Country) => {
    const nameA = (a.name[userCountry] || '').toUpperCase();
    const nameB = (b.name[userCountry] || '').toUpperCase();

    if (nameA < nameB) {
      return -1;
    }

    if (nameA > nameB) {
      return 1;
    }

    return 0;
  });

export const getOriginFromStorage = (): string => {
  let origin = '';
  if (storage.getExternalOrigin() != null) {
    origin = `&origin=${storage.getExternalOrigin()}`;
  }
  return origin;
};

export const getRegisterUrl = (path: string) =>
  `${REGISTER_DOMAIN}${path}?` + getOriginFromStorage();

export const getRegisterUrlWithHash = (path: string, productHash: string) => {
  return getRegisterUrl(path) + `&productHash=${productHash}`;
};

export const getPublicUrlByPath = (path: string) => `${PAYMENTS_ROOT_URL}${path}`;

export const getRegisterUrlToCheckout = (
  product: ProductTypes,
  currency: Currency,
  intervalBillingMode: IntervalBillingMode | null
) => {
  const intervalCounter = product.class === PRODUCT_CORE ? `/${intervalBillingMode}` : '';

  return `${PAYMENTS_ROOT_URL}account/checkout/${product.mdirectorId}/${currency}${intervalCounter}`;
};

export const getProductHash = (
  mdirectorId: string,
  currency: Currency,
  billingMode: BillingMode
): string =>
  btoa(
    `{"mdirectorId":"${mdirectorId}", "currency":"${currency}", "billingMode":"${billingMode}"}`
  );

export const getOffset = (el: any) => {
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left + window.scrollX,
    top: rect.top + window.scrollY,
  };
};

export const onlyDigits = (value: string) => /^\d+$/.test(value);

export const isNewFreeUser = (userData: UserData) => userData.rolId === ROL_ADM_MDIRECTOR_NEWFREE;
export const isNewFreeUserFromMe = (me: Me) => me.userInfo.rolId === ROL_ADM_MDIRECTOR_NEWFREE;
export const isAdminUser = (userData: UserData) => userData.rolId === ROL_ADM_MDIRECTOR;
export const isAdminUserFromMe = (me: Me) => me.userInfo.rolId === ROL_ADM_MDIRECTOR;
export const isAppSumo = (me: Me) => me.capabilities.appsumo.isAppsumo;
export const isEmBlue = (me: Me) => me.capabilities.isEmBlue;

export const getMyRateURL = () => `${PAYMENTS_ROOT_URL}account/${PRIVATE_NAVIGATION_TAB_RATE}`;

export const getMyAccountURL = () => `${PAYMENTS_ROOT_URL}account/${PRIVATE_NAVIGATION_MY_ACCOUNT}`;

export const redirectMyRate = () => window.location.assign(getMyRateURL());
export const redirectMyAccount = () => window.location.assign(getMyAccountURL());

export const isManualValidationNeeded = (me?: Me) =>
  typeof me !== 'undefined' && typeof me.capabilities.isManualValidationEnabled !== 'undefined'
    ? me.capabilities.isManualValidationEnabled
    : DEFAULT_MANUAL_VALIDATION_NEEDED;

export const checkHasToShowPhoneValidationForm = (mobileToken: MobileToken, me: Me): boolean => {
  if (eval(PHONE_VALIDATION_ACTIVATED as unknown as string) === false) {
    return false;
  }

  if (isAdminUserFromMe(me)) {
    return false;
  }

  if (mobileToken.usedAt) {
    return false;
  }

  return true;
};

export const getUserData = (me: Me | undefined, i18n: i18n): UserData => {
  return {
    name: me?.userInfo.name || '',
    groupId: me?.groupInfo.id || '',
    email: me?.userInfo.email || DEFAULT_MISSING_EMAIL,
    rolId: me?.userInfo.rolId || '',
    lang: i18n.language as AppLanguages,
    groupCreatedAt: me?.groupInfo.createdAt || '',
  };
};

export const getOrigin = (
  product: ProductTypes | undefined | null,
  billingMode?: BillingMode | null,
  defaultOrigin?: string
): string => {
  if (!product) return defaultOrigin || DEFAULT_ORIGIN_FORM;
  return getBillingModeOrigin(product, billingMode);
};

const getBillingModeOrigin = (product: ProductTypes, billingMode?: BillingMode | null): string => {
  if (billingMode) return `tarifa_${product.category}_${product.info.productStep}_${billingMode}`;

  if (!product?.prices[0]) return `tarifa_${product.category}_${product.info.productStep}`;
  const billingModeDefault = getBillingModeByIntervalCount(
    product?.prices[0]?.recurring?.interval_count as IntervalBillingMode
  );

  return `tarifa_${product.category}_${product.info.productStep}_${billingModeDefault}`;
};

export const isEmailMarketingTab = (tab: ProductCategoryTypes) =>
  tab === PRODUCT_CATEGORY_TYPE_EMAIL;

export const getProductToUpdateFromUserCollection = (
  userProductActive: UserProductTypes | null,
  userProducts: UserProductTypes[] | undefined
): UserProductTypes | null => {
  if (userProductActive) {
    return userProductActive;
  }

  // Order of the states to check:
  const statesToCheck = [CANCELLED, PENDING, PROCESSING];

  const products = statesToCheck.map((state) => {
    const product = filterUserProductsByStatus(userProducts, state as StatusType);
    if (product) {
      return product;
    }
  });

  return products.filter((item) => !!item)[0] || null;
};

export const shouldShowTheCurrencySwitch = (
  userProducts: UserProductTypes[] | undefined
): boolean => {
  const userProductActive = filterUserProductsByStatus(userProducts || [], ACTIVE);
  const userProductCancelled = filterUserProductsByStatus(userProducts || [], CANCELLED);
  return (
    (!userProductActive || isFreemiumProduct(userProductActive.product)) && !userProductCancelled
  );
};

export const shouldShowTheBillingModeSwitch = (): boolean => {
  // Perhaps this const will become a property in the future.
  return SHOW_ANNUAL_PAYMENTS;
};

export const filterHiddenPriceProducts = (
  products: ProductTypes[],
  mdirectorId: string | null
): ProductTypes => {
  const filteredProducts =
    products &&
    products.filter((product: ProductTypes) => {
      return product.mdirectorId === mdirectorId && !product.hidePrice;
    });

  return filteredProducts[0];
};

export const filterProducts = (
  products: ProductTypes[],
  mdirectorId: string | null
): ProductTypes => {
  const filteredProducts =
    products &&
    products.filter((product: ProductTypes) => {
      return product.mdirectorId === mdirectorId;
    });

  return filteredProducts[0];
};

export const getProductFilterPricesByCurrencyBillingMode = (
  product: ProductTypes,
  currency: Currency,
  billingMode: BillingMode
): ProductTypes => {
  const intervalCount = getIntervalCountByBillingMode(billingMode);

  if (intervalCount) {
    product.prices = product.prices.filter(
      (price: PricesTypes) =>
        price.currency === currency &&
        (price.recurring == null || price.recurring?.interval_count == intervalCount)
    );
  }

  return product;
};

export const getPricesIdsByCurrencyAndInterval = (
  prices: PricesTypes[],
  currency: Currency,
  intervalCount: IntervalBillingMode
): string[] => {
  return prices
    .filter(
      (price: PricesTypes) =>
        price.currency === currency &&
        (price.recurring == null || price.recurring?.interval_count == intervalCount)
    )
    .map((price: PricesTypes) => price.id);
};

export const getPromotionCodeByCurrencyAndInterval = (
  prices: PricesTypes[],
  currency: Currency,
  intervalCount: IntervalBillingMode
): string => {
  return (
    prices
      .filter(
        (price: PricesTypes) =>
          price.currency === currency &&
          price.promotionCode !== null &&
          (price.recurring == null || price.recurring?.interval_count == intervalCount)
      )
      .map((price: PricesTypes) => price.promotionCode && price.promotionCode.code)
      .pop() || ''
  );
};

export const sortPricesByUsage = (prices: PricesTypes[]): PricesTypes[] => {
  return prices.sort((a: PricesTypes, b: PricesTypes) => {
    const nameA = (a.recurring && a.recurring.usage_type) || '';
    const nameB = (b.recurring && b.recurring.usage_type) || '';
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
};

export const themes: { [key: string]: Theme } = {
  classicTheme: {
    assetsURL: `${THEME_ASSETS_URL}classicTheme`,
  },
  v2: {
    assetsURL: `${THEME_ASSETS_URL}v2/`,
  },
  v3: {
    assetsURL: `${THEME_ASSETS_URL}v3/`,
  },
};

export const getAppDashboardURL = () => `${APP_BACKEND_URL}private?redirect=dashboard`;
export const getEmblueHomeUrl = (companyId: string) =>
  APP_EMBLUE_MD_HOME_URL + '?company_id=' + companyId;

export const getAppUrlFromRol = (rolId: string) => {
  let redirect: string;
  switch (rolId) {
    case ROL_ADM_MDIRECTOR_NEWFREE:
    case ROL_ADM_MDIRECTOR:
    case ROL_STD_MDIRECTOR:
    case ROL_REPORT_MDIRECTOR:
    case ROL_ANALYST_MDIRECTOR:
    case ROL_NDC_MDIRECTOR:
      redirect = 'dashboard';
      break;
    case ROL_DB_MDIRECTOR:
      redirect = 'contacts';
      break;
    case ROL_CPM_MDIRECTOR:
    case ROL_CPU_MDIRECTOR:
    case ROL_GCYC_MDIRECTOR:
    case ROL_CPM_REPORT_MDIRECTOR:
    default:
      redirect = 'reports';
  }
  return `${APP_BACKEND_URL}private?redirect=${redirect}`;
};

export const findRawInvoicePreviewPrice = (data: InvoicePreviewTypes | null) => {
  if (!data || !data.items) {
    return null;
  }

  const price: InvoicePreviewItemTypes[] = data.items.filter((item: any) => item.amount < 0);

  if (!price[0] || !price[0].amount) {
    return 0;
  }

  const amount = price[0].amount;

  if (typeof amount === 'number' || typeof amount === 'bigint') {
    return amount;
  }

  return parseFloat(amount.replace(',', '.'));
};

export const findPromotionCodeByGroupProductAndProduct = (
  userProducts: UserProductTypes[],
  product: ProductTypes | null
): string => {
  const products =
    userProducts &&
    product &&
    userProducts.filter((userProduct: UserProductTypes) => {
      return (
        userProduct &&
        userProduct.product.id === product.id &&
        userProduct.groupProduct.promotionCode
      );
    });

  return (product && products && products[0] && products[0].groupProduct.promotionCode) || '';
};

export const redirectToAndReload = (urlRedirect: string) => {
  const newWindow = window.open(urlRedirect, '_blank');
  const reload = () => {
    const checkClosed = setInterval(() => {
      clearInterval(checkClosed);
      location.reload();
    }, 3000);
  };
  const checkClosed = setInterval(() => {
    if (newWindow && newWindow.closed) {
      clearInterval(checkClosed);
      reload();
    }
  }, 1000);
};

export const setterErrorPaymentModal = (
  setStatusPaymentError: any,
  setStatusUnpaidMeteredError: any,
  setStatusPendingPaymentError: any,
  status: number,
  detail?: string
) => {
  if (
    status === 503 ||
    status === 500 ||
    status === 402 ||
    (status === 409 &&
      detail &&
      detail === 'Cannot update a subscription with an incomplete payment')
  )
    return setStatusPaymentError(status);

  if (status === 409 && detail && detail === 'There are unpaid metered items in this subscription')
    return setStatusUnpaidMeteredError(status > 0);

  if (
    status === 409 &&
    detail &&
    detail === 'There are pending payments for the current subscription'
  )
    return setStatusPendingPaymentError(status > 0);
  return true;
};
